import { Box, Text } from '@ksyos/design-system';
import { useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import type { CustomControlStatus } from '../../../../common/enums/CustomControlStatus';
import type { ProcedureStatus } from '../../../../common/enums/ProcedureStatus';
import type { IRequest } from '../../../../common/interfaces/IRequest';
import type { QuestionnaireCustomControlData } from '../../../../common/validation/customControls/somnology';
import type { IReduxState, ISessionUser } from '../../../configureStore';
import { OsasQuestionnaireName } from '../../../../common/enums/OsasQuestionnaireName';
import { RequestStatus } from '../../../../common/enums/RequestStatus';
import { ServiceCode } from '../../../../common/enums/ServiceCode';
import { translateKey } from '../../../translate';
import { getIsOsasQuestionnaireComplete } from '../../../utils/getIsOsasQuestionnaireComplete';
import { PageTitle } from '../../elements/atoms/pageTitle/PageTitle';
import { Article } from '../../elements/molecules/article/article';
import { Card, CardTheme } from '../../elements/molecules/card/Card';
import { CenteredLoader } from '../../elements/molecules/centeredLoader/CenteredLoader';
import { Container } from '../../elements/molecules/container/Container';
import { Main } from '../../elements/molecules/main/Main';
import { PageSection } from '../../elements/molecules/pageSection/PageSection';
import { Footer } from '../../elements/organisms/footer/Footer';
import { Header } from '../../elements/organisms/header/Header';
import { OsasQuestionnaireCard } from '../../elements/organisms/osasCards/OsasQuestionnaireCard';
import { PageWrapper } from '../../elements/organisms/pageWrapper/PageWrapper';
import { QuestionnaireNotAvailable } from './questionnaires/QuestionnaireNotAvailable';
import { QuestionnairesSent } from './QuestionnairesSent';

export interface IOSASQuestionnaire {
    answer: QuestionnaireCustomControlData;
    dateSent: Date | string | null;
    name: OsasQuestionnaireName;
}

export enum ReportType {
    diagnostic = 'diagnostic',
    error = 'error',
    failure = 'failure',
}

interface INightlyReportMetadata {
    endDatetime: string;
    failureCode?: string;
    startDatetime: string;
    type: ReportType;
}

export interface IOSASWithQuestionnaires extends IRequest {
    procedure?: {
        customControlData: {
            nightlyReportMetadata?: { [key: string]: INightlyReportMetadata };
            questionnaires?: IOSASQuestionnaire[];
        };
        customControlStatus: CustomControlStatus;
        dateSent: string | null;
        id: string;
        status: ProcedureStatus;
    };
}

interface INewOsasQuestionnairesReduxProps {
    osasRequest?: IOSASWithQuestionnaires | null;
}

const mapStateToProps = ({ authentication }: IReduxState): INewOsasQuestionnairesReduxProps => ({
    osasRequest: getOsasRequest(authentication),
});

function getOsasRequest(authentication: ISessionUser) {
    if (authentication.requestProcesses?.length) {
        return (
            authentication.requestProcesses?.find(
                (request: IRequest) =>
                    [ServiceCode.OSASO, ServiceCode.OSASVO, ServiceCode.OSASQ].includes(request.service.code) &&
                    [RequestStatus.active, RequestStatus.completed].includes(request.status)
            ) ?? null
        );
    }
}

export function NewOsasQuestionnairesWrapper(props: INewOsasQuestionnairesReduxProps) {
    const [searchParams] = useSearchParams();
    const procedureId = searchParams.get('procedureId');

    const [shouldDisplayHsdq, setShouldDisplayHsdq] = useState(false);

    useEffect(() => {
        if (props.osasRequest) {
            setShouldDisplayHsdq(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).ksyosSettings.hsdqQuestionnaireEnabled &&
                    (props.osasRequest.service.code === ServiceCode.OSASO ||
                        props.osasRequest.customControlData.isHsdqQuestionnaireSentToThePatient)
            );
        }
    }, [props.osasRequest]);

    if (props.osasRequest === undefined) {
        return <CenteredLoader size="large" />;
    }

    if (!procedureId || !props.osasRequest?.procedure?.id || props.osasRequest?.procedure?.id !== procedureId) {
        return <QuestionnaireNotAvailable />;
    }

    const questionnaires = props.osasRequest.procedure.customControlData?.questionnaires;
    const isFosq10Sent = getIsOsasQuestionnaireComplete(OsasQuestionnaireName.fosq10, questionnaires);
    const isEssSent = getIsOsasQuestionnaireComplete(OsasQuestionnaireName.ess, questionnaires);
    let isHsdqSent = true;
    if (shouldDisplayHsdq) {
        isHsdqSent = getIsOsasQuestionnaireComplete(OsasQuestionnaireName.hsdq, questionnaires);
    }
    const [welcomeCardTitle, welcomeCardChildren] = getWelcomeCardTexts();

    function getWelcomeCardTexts() {
        const areQuestionnairesSent = shouldDisplayHsdq
            ? [isEssSent, isFosq10Sent, isHsdqSent]
            : [isEssSent, isFosq10Sent];
        const numberOfQuestionnairesSent = areQuestionnairesSent
            .map((value: boolean) => (value ? 1 : 0) as number)
            .reduce((a, b) => a + b);
        let title = 'Hoe gaat het met je slaapklachten?';
        let children = getInitialChildrenText();

        if (areQuestionnairesSent.length === 3) {
            if (numberOfQuestionnairesSent === 1) {
                title = 'Je hebt een vragenlijst afgerond';
                children = <Text className="mb-2">Nog twee vragenlijsten te gaan.</Text>;
            } else if (numberOfQuestionnairesSent === 2) {
                title = 'Nog één vragenlijst te gaan';
                children = <Text className="mb-2">Bijna klaar!💪 </Text>;
            }
        }

        if (areQuestionnairesSent.length === 2 && numberOfQuestionnairesSent === 1) {
            title = 'Nog één vragenlijst te gaan';
            children = <Text className="mb-2">Bijna klaar!💪 </Text>;
        }

        return [title, children];
    }

    return (
        <PageWrapper fullHeight>
            <Header
                backButtonText={translateKey('common.home')}
                backUrl="/"
                hasBackbutton
                hasLogoutButton
            />
            <Main>
                {isFosq10Sent && isEssSent && isHsdqSent ? (
                    <QuestionnairesSent serviceCode={props.osasRequest.service.code} />
                ) : (
                    getContentWithQuestionnaires()
                )}
            </Main>
            <Footer />
        </PageWrapper>
    );

    function getContentWithQuestionnaires() {
        return (
            <Container isFluidOnMobile>
                <PageSection>
                    <Article>
                        <PageTitle>Vragenlijsten Slaap</PageTitle>
                    </Article>
                    <Card
                        body=""
                        cardTitle={welcomeCardTitle as string}
                        theme={CardTheme.white}
                    >
                        {welcomeCardChildren}
                    </Card>
                </PageSection>

                <PageSection>
                    <Box
                        display="grid"
                        gap={4}
                        gridTemplateColumns={['1fr', null, '1fr 1fr']}
                    >
                        {shouldDisplayHsdq && (
                            <OsasQuestionnaireCard
                                body="Nog te doen | 5 minuten"
                                className={'automation-HSDQ'}
                                isSent={isHsdqSent}
                                title="HSDQ vragenlijst"
                                to={`/questionnaire/request?procedureId=${procedureId}&name=${OsasQuestionnaireName.hsdq}`}
                            />
                        )}

                        <OsasQuestionnaireCard
                            body="Nog te doen | 2 minuten"
                            className={'automation-FOSQ_10'}
                            isSent={isFosq10Sent}
                            title="FOSQ-10 vragenlijst"
                            to={`/questionnaire/request?procedureId=${procedureId}&name=${OsasQuestionnaireName.fosq10}`}
                        />
                        <OsasQuestionnaireCard
                            body="Nog te doen | 2 minuten"
                            className={'automation-ESS'}
                            isSent={isEssSent}
                            title="ESS vragenlijst"
                            to={`/questionnaire/request?procedureId=${procedureId}&name=${OsasQuestionnaireName.ess}`}
                        />
                    </Box>
                </PageSection>
            </Container>
        );
    }

    function getInitialChildrenText() {
        return props.osasRequest?.service.code === ServiceCode.OSASQ ? (
            <Text className="mb-2">
                Vul de vragenlijsten in voor je volgende afspraak met de slaaparts en laat weten hoe het met je gaat.
            </Text>
        ) : (
            <>
                <Text className="mb-2">Je hebt één of meerdere nachten met de slaaprecorder geslapen. </Text>
                <Text className="mb-2">
                    Om een consult met je in te kunnen plannen hebben we wat informatie nodig.{' '}
                </Text>
                <Text className="mb-2">Vul de vragenlijsten in en laat weten hoe het met je gaat.</Text>
            </>
        );
    }
}

export const NewOsasQuestionnaires = connect(mapStateToProps)(NewOsasQuestionnairesWrapper);
